import React, { useEffect } from 'react';
import { Upload as UploadIcon, FileText } from 'lucide-react';
import { FileTransformedToString, Upload } from 'src/shared/components/upload';
import useProjectDefinition from 'src/app/projects/views/new-project-definition/hooks/useProjectDefinition';
import { validateHabitatInsightsGeoJSON } from 'src/app/habitat-insights/views/habitat-insights-details/components/habitat-insights-upload/habitatInsightsUploadHelpers';
import { HabitatInsightsGeoJsonFile } from 'src/shared/types';
import { SecondaryButton, PrimaryButton } from 'src/shared/components/button/Button';
import Modal from 'src/shared/components/modal/Modal';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import styles from './HabitatInsightsSamplesUpload.module.scss';

export const HabitatInsightsSamplesUpload = () => {
    const { onFormChange, formState } = useProjectDefinition();
    const [showModal, setShowModal] = React.useState(false);

    const { habitatInsightsUploadedFileErrors, habitatInsightsUploadedFileContent, habitatInsightsUploadedFileName } = formState;

    const onUpload = (file: FileTransformedToString) => {
        onFormChange({
            habitatInsightsUploadedFileContent: file.content || '',
            habitatInsightsUploadedFileErrors: file.content ? validateHabitatInsightsGeoJSON(file.content) : [],
            habitatInsightsUploadedFileName: file.filename || '',
        });
    };

    useEffect(() => {
        if (habitatInsightsUploadedFileContent && !habitatInsightsUploadedFileErrors.length) {
            try {
                const habitatInsightsSamplesData: HabitatInsightsGeoJsonFile = JSON.parse(habitatInsightsUploadedFileContent);
                if (habitatInsightsSamplesData && habitatInsightsSamplesData.features) {
                    onFormChange({
                        habitatInsightsSites: habitatInsightsSamplesData.features,
                    });
                }
            } catch (_error) {
                // Invalid JSON, ignore
            }
        }
    }, [habitatInsightsUploadedFileContent, habitatInsightsUploadedFileErrors, onFormChange]);

    const handleReset = () => {
        onFormChange({
            habitatInsightsUploadedFileContent: '',
            habitatInsightsUploadedFileErrors: [],
            habitatInsightsUploadedFileName: '',
            habitatInsightsSites: [],
        });
    };

    return (
        <div className={styles.uploadWrapper}>
            <div className={styles.top}>
                <div className={styles.topLeft}>
                    <div className={styles.uploadIcon}>
                        <UploadIcon />
                    </div>
                    <div className={styles.uploadTitle}>Upload data</div>
                    <div className={styles.uploadFileType}>Supported file type: .geojson</div>
                </div>
                <div className={styles.topRight}>
                    <QuestionButtonHelp
                        type='api'
                        placement='right'
                        slug='how-do-i-prepare-my-file-for-upload'
                        label='How do I prepare my file for upload?'
                    />
                </div>
            </div>
            {habitatInsightsUploadedFileContent.length > 0 && !habitatInsightsUploadedFileErrors.length ? (
                <div className={styles.uploadedFileContainer}>
                    <div className={styles.uploadedFileIconContainer}>
                        <FileText size={24} />
                        <div>
                            <div>Uploaded file:</div>
                            <div className={styles.uploadedFileNameText}>{habitatInsightsUploadedFileName}</div>
                        </div>
                    </div>
                    <div>
                        <SecondaryButton onClick={() => setShowModal(true)}>Replace with new data upload</SecondaryButton>
                    </div>
                </div>
            ) : (
                <div className={styles.bottom}>
                    <div className={styles.uploadInput}>
                        <Upload onUpload={onUpload} transformTo='text' accept='application/geo+json,.geojson' />
                    </div>
                </div>
            )}
            {showModal && (
                <Modal>
                    <div className={styles.modalContainer}>
                        <div className={styles.title}>Are you sure you want to replace this data with a new upload?</div>
                        <div className={styles.subTitle}>The existing data will be deleted</div>
                        <div className={styles.buttonContainer}>
                            <SecondaryButton onClick={() => setShowModal(false)}>Cancel</SecondaryButton>
                            <PrimaryButton
                                onClick={() => {
                                    setShowModal(false);
                                    handleReset();
                                }}
                            >
                                Yes
                            </PrimaryButton>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default HabitatInsightsSamplesUpload;
